import React, { useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import {Autocomplete, InputAdornment, TextField} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import styles from './SearchInput.module.scss';

interface SearchInputProps {
    items: string[];
    placeholder?: string;
    onChange: (value: string) => void;
    onSubmit: (value: string) => void;
}


export const SearchInput: React.FC<SearchInputProps> = ({ items = [], placeholder = '', onChange, onSubmit }) => {
    const searchTermRef = useRef('');
    const inputRef = useRef<HTMLInputElement>(null);
    const rafIdRef = useRef(0);

    const debouncedChangeHandler = useRef(
        debounce((value: string) => {
            onChange(value);
        }, 500)
    ).current;

    useEffect(() => {
        return () => {
            debouncedChangeHandler.cancel();
            cancelAnimationFrame(rafIdRef.current);
        };
    }, [debouncedChangeHandler]);

    const handleInputChange = (_event: React.SyntheticEvent, value: string, reason: string) => {
        if (reason === 'input') {
            searchTermRef.current = value;
            debouncedChangeHandler(value);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = requestAnimationFrame(() => {
                const input = inputRef.current;
                if (!input) return;
                const activeId = input.getAttribute('aria-activedescendant');
                if (activeId) {
                    const activeEl = document.getElementById(activeId);
                    if (activeEl?.textContent) {
                        input.value = activeEl.textContent;
                    }
                } else {
                    input.value = searchTermRef.current;
                }
            });
        }
    };

    const handleSubmit = (value: string) => {
        value = value ? value.toLowerCase().trim().replace("'", '') : value;
        onSubmit(value);
    };

    return (
        <div className="autocomplete__container">
            <Autocomplete
                freeSolo
                className={styles.searchInput}
                onInputChange={handleInputChange}
                options={items}
                filterOptions={() => items}
                getOptionLabel={(option) => option}
                renderInput={(params) => {
                    const muiInputRef = (params.inputProps as any).ref;
                    return (
                        <TextField
                            {...params}
                            variant="outlined"
                            fullWidth
                            placeholder={placeholder || 'Search'}
                            inputProps={{
                                ...params.inputProps,
                                ref: (el: HTMLInputElement | null) => {
                                    inputRef.current = el;
                                    if (typeof muiInputRef === 'function') muiInputRef(el);
                                    else if (muiInputRef) muiInputRef.current = el;
                                },
                                onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                    (params.inputProps as any).onKeyDown?.(e);
                                    handleKeyDown(e);
                                },
                            }}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    );
                }}
                onChange={(_, newValue) => {
                    if (newValue) {
                        handleSubmit(newValue);
                    }
                }}
            />
        </div>
    );
};
